/**
 * The read-only atlas (spec §22.3) — destination search and destination pages.
 *
 * Anonymous throughout: a destination page is readable and shareable without an
 * account, because the guide is the pitch.
 */
import type { PhotoAttribution } from '../types';
import { fetchApi } from './apiBase';

export interface DestinationCard {
  slug: string;
  name: string;
  country: string;
  /** The first-level admin area ("MA") — blank outside the US or on old rows. */
  region: string;
  /** The SERVED label ("Chatham, MA" / "Granada, Spain") — render this, never
   *  re-join name and country: three local copies of that join are how the
   *  Sep-7 label bug would have needed fixing three times. */
  where: string;
  /** The same rule WITHOUT the name — "MA", "Spain", '' — for surfaces that
   *  print the two on separate lines (a hero) or as a subtitle (a row). */
  where_sub: string;
  dest_type: 'locality' | 'region';
  thumb_url: string;
  /** Who took `thumb_url` — see `SearchedDestination.thumb_attribution`. */
  thumb_attribution: PhotoAttribution | null;
}

export interface GuideEdition {
  edition_number: number;
  title: string;
  duration_days: number;
  days: Array<{
    day: number;
    label?: string;
    activities: Array<{ name: string; time?: string; note?: string; location?: string }>;
  }>;
  created_at: string;
}

export interface DestinationDetail
  extends Omit<DestinationCard, 'thumb_url' | 'thumb_attribution'> {
  latitude: number | null;
  longitude: number | null;
  hero_url: string;
  /** Who took `hero_url`. Null means nobody is owed a credit — no photograph,
   *  or a first-party asset. A pinned edition whose photographer can no longer
   *  be named serves NEITHER: the page shows no photo rather than an
   *  uncreditable one. */
  hero_attribution: PhotoAttribution | null;
  guide: GuideEdition | null;
  plan_count: number;
  /** False when reading a pinned edition that newer editions have superseded. */
  is_current_edition: boolean;
  current_edition_number: number | null;
  /** The reader already has an unspent save of this edition. */
  is_saved: boolean;
  /** Present when the requested slug was RENAMED — the client should correct
   *  its address. A merge is different: see `merged_into`. */
  redirected_from?: string;
  /** Set when reading a pinned edition of a destination that has since been
   *  merged away. The page stays here — the edition belongs to this row — but
   *  says where the place now lives. */
  merged_into?: { slug: string; name: string };
}

export type GenerationStatus =
  | 'requested' | 'generating' | 'requester_visible' | 'checks_running'
  | 'published' | 'failed' | 'held_for_review';

export interface GenerationPayload {
  generation_id: number;
  destination_slug: string;
  status: GenerationStatus;
  can_save: boolean;
  is_draft: boolean;
  created?: boolean;
  edition_number?: number;
  failure_reason?: string;
  guide?: Omit<GuideEdition, 'edition_number' | 'created_at'>;
}

export function requestGeneration(
  slug: string, requestKey: string,
): Promise<GenerationPayload> {
  return fetchApi<GenerationPayload>(`/api/plans/destinations/${slug}/generate/`, {
    method: 'POST', body: JSON.stringify({ request_key: requestKey }),
  });
}

export function fetchGeneration(
  slug: string, generationId?: number,
): Promise<GenerationPayload> {
  const query = generationId === undefined
    ? '' : `?generation_id=${encodeURIComponent(String(generationId))}`;
  return fetchApi<GenerationPayload>(
    `/api/plans/destinations/${slug}/generation/${query}`,
  );
}

/**
 * What analytics may see — never the query text (§22.6).
 *
 * `outcome` is null only when the query was too short to search: nothing was
 * looked up, so nothing is measured. The approved vocabulary is exactly
 * hit | ambiguous | miss, and a fourth value would skew every funnel
 * denominator that divides by "searches".
 */
export interface DestinationSearchResponse {
  results: DestinationCard[];
  outcome: 'hit' | 'ambiguous' | 'miss' | null;
  query_type?: 'place_like' | 'free_text';
  /** The slug the atlas actually resolved to — null when it could not say. */
  resolved_slug?: string | null;
  query_len: number;
  too_short: boolean;
  min_query_len: number;
}

export function searchDestinations(query: string): Promise<DestinationSearchResponse> {
  return fetchApi<DestinationSearchResponse>(
    `/api/plans/destinations/search/?q=${encodeURIComponent(query)}`,
  );
}

/**
 * Fetch a destination. Passing `edition` pins the read to that edition, which
 * keeps rendering after newer ones exist — a shared URL whose content silently
 * changes is one nobody can cite (§22.1).
 */
export function fetchDestination(
  slug: string, edition?: number,
): Promise<DestinationDetail> {
  const query = edition === undefined ? '' : `?edition=${edition}`;
  return fetchApi<DestinationDetail>(`/api/plans/destinations/${slug}/${query}`);
}

/**
 * The plans a destination page's own count promises (§22.7's Explore↔Plan
 * bridge). Keyed by SLUG, not name: slugs are immutable and a name is
 * ambiguous, and this endpoint resolves through the destination FK so its
 * `count` is the same number the page advertises.
 */
export interface DestinationPlansResponse {
  /** The unfiltered count, for §1.8's "3 of 7 plans match". Equals `count`
   *  when no filter is active. */
  total: number;
  results: PlanCard[];
  count: number;
  destination: { slug: string; name: string };
  /** Present when the slug asked for has moved; the client should correct it. */
  redirected_from?: string;
}

export interface PlanCard {
  id: number;
  title: string;
  slug: string;
  destination: string;
  duration_days: number;
  season: string;
  group_type: string;
  /** Real uses: public, live copies; 0 renders nothing. */
  used_count: number;
  highlights: string[];
  hero_thumb_url: string;
  is_staff_pick: boolean;
  score: number;
  photo_query_key: string;
  creator_name: string;
}

/** §1.8's filters travel: the map's chips come here so "the pin's number and
 *  the page a reader lands on never disagree". The server applies the SAME
 *  predicate the map counted with, and answers with both numbers — `count`
 *  matching, `total` unfiltered — so the headline needs no second request. */
/** One query against `/api/plans/search/` — the unified endpoint the search box
 *  moved to in slice 4c. Destinations AND published plans in one response,
 *  with the SAME outcome vocabulary as the endpoint it replaces, so the funnel
 *  events do not change shape when the box moves (§22.6: what was FOUND, never
 *  what was asked). */
export interface SearchedDestination {
  slug: string;
  name: string;
  country: string;
  /** The first-level admin area ("MA") — blank outside the US or on old rows. */
  region: string;
  /** The SERVED label ("Chatham, MA" / "Granada, Spain") — render this, never
   *  re-join name and country: three local copies of that join are how the
   *  Sep-7 label bug would have needed fixing three times. */
  where: string;
  /** The same rule WITHOUT the name — "MA", "Spain", '' — for surfaces that
   *  print the two on separate lines (a hero) or as a subtitle (a row). */
  where_sub: string;
  /** Nullable: the atlas allows coordinateless rows, and §1.6's fly-to-hit
   *  needs to know there is nowhere to fly to. */
  lat: number | null;
  lng: number | null;
  thumb_url: string;
  /** Who took `thumb_url`. Travels with it — a row that renders the image
   *  without this credits nobody, which the licence does not allow. Null means
   *  there is nothing to credit: no photograph, or one we own. */
  thumb_attribution: PhotoAttribution | null;
  /** Whether the destination would draw a pin (current edition or at least one
   *  published plan — the map's own §1.2 rule). A map-view hit flies and raises
   *  the destination's card, and an unpinned destination HAS no card: flying
   *  there would promise something the sheet cannot show, where its page can. */
  pinned: boolean;
}

export interface SearchedPlan {
  slug: string;
  title: string;
  destination: string;
  duration_days: number;
  hook: string;
  /** Present and null rather than absent when a plan never resolved to an
   *  atlas row — a pinless plan is what this endpoint keeps findable, and the
   *  client must tell the two apart to know whether a tap can fly the map. */
  destination_slug: string | null;
}

export interface ExploreSearchResponse {
  destinations: SearchedDestination[];
  plans: SearchedPlan[];
  outcome: 'hit' | 'ambiguous' | 'miss' | null;
  /** The same closed vocabulary as the endpoint this one replaced — typed
   *  identically on purpose, so the analytics call cannot silently widen. */
  query_type?: 'place_like' | 'free_text';
  resolved_slug?: string | null;
  query_len: number;
  too_short: boolean;
  min_query_len: number;
}

export function fetchExploreSearch(query: string): Promise<ExploreSearchResponse> {
  return fetchApi<ExploreSearchResponse>(
    `/api/plans/search/?q=${encodeURIComponent(query)}`);
}

export function fetchDestinationPlans(
  slug: string,
  filters: { length?: string; group?: string; band?: string } = {},
): Promise<DestinationPlansResponse> {
  const params = new URLSearchParams();
  if (filters.length) params.set('length', filters.length);
  if (filters.group) params.set('group', filters.group);
  if (filters.band) params.set('band', filters.band);
  const query = params.toString();
  return fetchApi<DestinationPlansResponse>(
    `/api/plans/destinations/${slug}/plans/${query ? `?${query}` : ''}`);
}

/** A guide someone kept, before it has dates (spec §22.3). */
export interface SavedTrip {
  id: number;
  title: string;
  duration_days: number;
  days: GuideEdition['days'];
  is_customized: boolean;
  destination_slug: string;
  destination_name: string;
  country: string;
  /** The destination's served label and its qualifier — a saved row asks
   *  the same "which one is this" question as its page (r763). */
  where: string;
  where_sub: string;
  edition_number: number;
  hero_url: string;
  is_scheduled: boolean;
  itinerary: number | null;
  scheduled_at: string | null;
  created_at: string;
}

/**
 * Keep a guide for later. Passing `edition` pins the save to the edition the
 * reader actually read — saving what is on screen, not what is current by the
 * time the request lands.
 */
export function saveDestination(slug: string, edition?: number): Promise<SavedTrip> {
  return fetchApi<SavedTrip>(`/api/plans/destinations/${slug}/save/`, {
    method: 'POST',
    body: JSON.stringify(edition === undefined ? {} : { edition }),
  });
}

export function fetchSavedTrips(includeScheduled = false): Promise<{ results: SavedTrip[] }> {
  const q = includeScheduled ? '?include_scheduled=1' : '';
  return fetchApi<{ results: SavedTrip[] }>(`/api/plans/saved/${q}`);
}

export function deleteSavedTrip(id: number): Promise<void> {
  return fetchApi<void>(`/api/plans/saved/${id}/`, { method: 'DELETE' });
}

/** Give a save dates. This is what creates a real, dated plan. */
export function scheduleSavedTrip(
  id: number, startDate: string,
): Promise<{ itinerary: { id: number }; saved_trip: SavedTrip }> {
  return fetchApi(`/api/plans/saved/${id}/schedule/`, {
    method: 'POST',
    body: JSON.stringify({ start_date: startDate }),
  });
}

/**
 * A save that could not happen because nobody was signed in.
 *
 * Only a REFERENCE is held — slug and edition — never content: the guide lives
 * on the server, so signup replays "save this edition" rather than uploading a
 * plan the client could have altered. That is what makes this simpler than the
 * chat carry-through, where the transcript genuinely only exists client-side.
 */
const PENDING_SAVE_KEY = 'vizday_pending_save';
const PENDING_SAVE_TTL_MS = 3600_000;

/**
 * `ts` is the moment the INTENT was formed, and a re-queue after a failed
 * replay must pass the original back. Stamping a fresh one restarts the hour,
 * which turns "drop it after an hour" into "retry it forever" the moment a
 * replay fails permanently — a slug that no longer resolves would be retried
 * on every app load for the life of the browser profile.
 */
export function queuePendingSave(
  slug: string, edition: number | null, ts: number = Date.now(),
): void {
  try {
    localStorage.setItem(PENDING_SAVE_KEY, JSON.stringify({ slug, edition, ts }));
  } catch {
    // Private mode, quota — the save is simply not resumed.
  }
}

/**
 * A destination slug, exactly as Django mints them.
 *
 * This is a security boundary, not tidiness: the value comes from
 * localStorage and is interpolated straight into a request path, so an
 * unvalidated one traverses. `../../../api/planner/itineraries/1/archive/#`
 * normalizes to an authenticated, CSRF-bearing POST to somebody's archive
 * endpoint — a stored value turning into an arbitrary action on the account
 * that happens to sign in next.
 */
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export interface PendingSave {
  slug: string;
  edition: number | null;
  /** When the intent was formed — carry it through any re-queue. */
  ts: number;
}

export function takePendingSave(): PendingSave | null {
  try {
    const raw = localStorage.getItem(PENDING_SAVE_KEY);
    if (!raw) return null;
    localStorage.removeItem(PENDING_SAVE_KEY);
    const intent = JSON.parse(raw);
    if (typeof intent?.slug !== 'string' || !SLUG_RE.test(intent.slug)) return null;
    // A non-finite ts makes every comparison below NaN, so neither expiry
    // fires and the intent lives forever — the exact failure the TTL exists
    // to prevent, reachable by writing a string into storage.
    if (typeof intent.ts !== 'number' || !Number.isFinite(intent.ts)) return null;
    const edition = intent.edition;
    if (edition !== null && edition !== undefined
        && !(typeof edition === 'number' && Number.isInteger(edition))) return null;

    // Stale intents are dropped rather than replayed: silently saving
    // something a person looked at last week is not resuming their action.
    // A ts in the future means a clock change, not a fresh intent.
    const age = Date.now() - intent.ts;
    if (age > PENDING_SAVE_TTL_MS || age < 0) return null;
    return { slug: intent.slug, edition: edition ?? null, ts: intent.ts };
  } catch {
    return null;
  }
}
